import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type SurveyViewedAction } from '@/models/analytics/features/survey';
import {
  SCRIBE_LAYOUT_CSAT_FEEDBACK_TARGET,
  SCRIBE_LAYOUT_CSAT_SURVEY_ACTION,
  type ScribeLayoutCsatAnalyticsPorts,
  type ScribeLayoutCsatFeedbackTargetToken,
  type ScribeLayoutCsatSurveyActionToken,
  type TrackScribeLayoutCsatSubmitted,
  type TrackScribeLayoutCsatViewed,
} from '@heidi/growth/contracts';
import { type FeedbackTargetType } from '@heidi/schema/ml-scribe';

/**
 * The app-side emit host for the scribe-layout CSAT popover (`@heidi/growth`).
 *
 * Ruling 24: the popover decides WHEN each event fires — the once-per-open
 * impression guard, and a submit only once the form resolves — and supplies the
 * payload; these two functions add the `event_name`, the destination routing, and
 * nothing else.
 *
 * ROUTING IS PRESERVED, NOT TIDIED: both call sites passed `{ amplitude: false }`
 * and that is reproduced exactly.
 */

/**
 * The package's tokens bound to the app vocabularies that declare them.
 *
 * `survey_viewed` and `feedback_submitted` are cross-domain — `feedback_submitted`
 * alone is reported by eight surfaces across chat, evidence, the document inbox
 * and the editor — so neither name is the growth domain's to declare (D9 ruling
 * 27) and both vocabularies stay app-side. These maps are the one place the
 * package's tokens meet them.
 *
 * NEITHER TARGET IS A TypeScript ENUM, and that is why these are maps rather than
 * plain pass-throughs. `SurveyViewedAction` is a string-literal union and
 * `target_type` is `FeedbackTargetType` widened by four FE-only strings, so a
 * package token would structurally assign into both and the check would be
 * vacuous — a token could drift to a string neither union has and only fail at the
 * emit. `satisfies Record<Token, …>` makes each map TOTAL, so the drift fails at
 * the binding instead. `trackScribeLayoutCsatAnalytics.test.ts` asserts the values
 * as strings on top, because a structural check cannot tell two spellings apart
 * once both are members.
 */
const SURVEY_ACTION_BY_TOKEN = {
  [SCRIBE_LAYOUT_CSAT_SURVEY_ACTION.shown]: 'scribe_layout_csat_shown',
} as const satisfies Record<ScribeLayoutCsatSurveyActionToken, SurveyViewedAction>;

const FEEDBACK_TARGET_BY_TOKEN = {
  [SCRIBE_LAYOUT_CSAT_FEEDBACK_TARGET.scribeLayoutV2]: 'scribe_layout_v2',
} as const satisfies Record<
  ScribeLayoutCsatFeedbackTargetToken,
  FeedbackTargetType | 'scribe_layout_v2'
>;

/** The popover became visible. */
export const trackScribeLayoutCsatViewed: TrackScribeLayoutCsatViewed = ({
  view_action,
}) => {
  track({
    event_name: TrackingEventName.SurveyViewed,
    props: { view_action: SURVEY_ACTION_BY_TOKEN[view_action] },
    options: { amplitude: false },
  });
};

/**
 * A star rating was submitted.
 *
 * `feedback` is passed STRAIGHT THROUGH, not spread conditionally, so a blank
 * comment reports a PRESENT `feedback: undefined` key. That is what the pre-move
 * call site sent — it built `feedback: values.comment?.trim() || undefined`
 * unconditionally — and the key's presence is part of the wire shape, so do not
 * "tidy" this into a conditional spread. Pinned by the `feedback as undefined`
 * arm of `trackScribeLayoutCsatAnalytics.test.ts`.
 */
export const trackScribeLayoutCsatSubmitted: TrackScribeLayoutCsatSubmitted = ({
  value,
  feedback,
  target_type,
}) => {
  track({
    event_name: TrackingEventName.FeedbackSubmitted,
    props: {
      type: 'rating',
      value,
      feedback,
      target_type: FEEDBACK_TARGET_BY_TOKEN[target_type],
    },
    options: { amplitude: false },
  });
};

/**
 * Both seams as one bundle, for the surface that composes the popover once its
 * root makes its class-A move.
 *
 * A module constant rather than a `useMemo`, so it is the same object on every
 * render and cannot be the thing that re-fires the impression effect.
 */
export const scribeLayoutCsatAnalyticsPorts: ScribeLayoutCsatAnalyticsPorts = {
  trackViewed: trackScribeLayoutCsatViewed,
  trackSubmitted: trackScribeLayoutCsatSubmitted,
};
